/* eslint-disable local-rules/interfaces-types-properties-naming */
import type { ReactSDKClient } from '@optimizely/react-sdk';
import { createInstance, enums } from '@optimizely/react-sdk';

import {
	triggerOptimizelyDecisionEvent,
	triggerOptimizelyTrackingEvent,
} from 'Services/analytics/events/optimizelyEvents';
import { logger } from 'Services/logger/logger';

export type ConfigDatafile = object | string;

export type OptimizelyDecisionEventProps = {
	decision: string;
	flag_enabled: boolean;
	flag_name: string;
	optimizely_user_id: string;
	rule_name: string;
};

export type OptimizelyTrackEventProps = {
	datafile_version: string;
	event_name: string;
	optimizely_user_id: string;
	sdk_name: string;
};

const OPTIMIZELY_EVENT = {
	DECISION: (data: OptimizelyDecisionEventProps) => triggerOptimizelyDecisionEvent(data),
	TRACK: (data: OptimizelyTrackEventProps) => triggerOptimizelyTrackingEvent(data),
};

export type OnDecisionProps = {
	decisionInfo: {
		decisionEventDispatched: boolean;
		enabled: boolean;
		flagKey: string;
		reasons: string[];
		ruleKey: string;
		variables: object;
		variationKey: string;
	};
	type: string;
	userId: string;
};

export const onDecision = ({ decisionInfo, type, userId }: OnDecisionProps): void => {
	if (type === 'flag' && decisionInfo?.decisionEventDispatched && decisionInfo?.enabled) {
		OPTIMIZELY_EVENT.DECISION({
			decision: decisionInfo.variationKey,
			flag_enabled: decisionInfo.enabled,
			flag_name: decisionInfo.flagKey,
			optimizely_user_id: userId,
			rule_name: decisionInfo.ruleKey,
		});
	}
};

export type OnTrackProps = {
	eventKey: string;
	eventTags?: unknown;
	logEvent: {
		httpVerb: string;
		params: {
			account_id: string;
			client_name: string;
			client_version: string;
			enrich_decisions: boolean;
			project_id: string;
			revision: string;
			visitors: unknown;
		};
		url: string;
	};
	userId: string;
};

export const onTrack = ({ eventKey, logEvent, userId }: OnTrackProps): void => {
	OPTIMIZELY_EVENT.TRACK({
		datafile_version: logEvent.params.revision,
		event_name: eventKey,
		optimizely_user_id: userId,
		sdk_name: logEvent.params.client_name,
	});
};

/**
 * A usable Optimizely datafile is either a non-empty config object or a string
 * that parses as JSON. The datafile CDN/S3 can respond with a non-JSON error
 * body (e.g. an S3 `AccessDenied` XML document when credentials are missing
 * locally). Feeding that to `createInstance` produces a client that can never
 * become ready, which then emits "client not ready" warnings on every render —
 * flooding the logs and slowing the dev server. Treat such responses as "no
 * datafile" so the SDK is never initialised with them.
 */
export const isUsableOptimizelyDatafile = (datafile: ConfigDatafile | null | undefined): boolean => {
	if (!datafile) {
		return false;
	}

	// Config objects (including the empty `{}` used by Mirage in tests) keep their
	// previous behaviour — only string bodies are validated, since that is where
	// the malformed CDN/S3 error responses arrive.
	if (typeof datafile === 'object') {
		return true;
	}

	try {
		const parsed = JSON.parse(datafile);

		return typeof parsed === 'object' && parsed !== null;
	} catch {
		return false;
	}
};

// Tracks whether a usable Optimizely datafile is configured for the current
// environment. When it is not (e.g. local dev without an SDK key, or an error
// response from the datafile CDN), the SDK and react-sdk emit the same warnings
// on every render, which floods the logs — especially on the vehicle list where
// cards re-render on bidding updates.
let isOptimizelyDatafileConfigured = false;

const OPTIMIZELY_NO_DATAFILE_NOISE = [
	'must be supplied via a parent <OptimizelyProvider>',
	'You must provide at least one of sdkKey or datafile',
];

export const optimizelyLogger = (_: unknown, message: string): void => {
	// This is used to prevent us logging when we override the bucketing_id in the provider
	if (message === 'DECISION_SERVICE: BucketingID attribute is not a string. Defaulted to userId') {
		return;
	}

	// Expected noise when Optimizely is not configured (no datafile). In deployed
	// environments a datafile IS present, so these still surface if they ever occur.
	if (!isOptimizelyDatafileConfigured && OPTIMIZELY_NO_DATAFILE_NOISE.some((noise) => message.includes(noise))) {
		return;
	}

	logger.warn({
		message: `[Optimizely] - ${message}`,
		scope: 'optimizelyInstanceFactory',
	});
};

export const optimizelyInstanceFactory = (datafile: ConfigDatafile): ReactSDKClient => {
	isOptimizelyDatafileConfigured = Boolean(
		datafile && (typeof datafile !== 'object' || Object.keys(datafile).length > 0),
	);

	return createInstance({
		datafile,
		eventBatchSize: 10,
		eventFlushInterval: 1000,
		logger: {
			log: optimizelyLogger,
		},
		logLevel: enums.LOG_LEVEL.WARNING,
		odpOptions: {
			disabled: true,
		},
	});
};

interface GetUserAttributesPayloadParams {
	dealerContactId?: number;
	dealershipId?: number;
}

export const getUserAttributesPayload = ({
	dealerContactId,
	dealershipId,
}: GetUserAttributesPayloadParams): Record<string, Nullable<string>> => {
	if (!dealerContactId || !dealershipId) {
		return {};
	}

	return {
		$opt_bucketing_id: `${dealershipId}`,
		dealership_and_contact_id_concat: `${dealershipId}_${dealerContactId}`,
		dealership_id: `${dealershipId}`,
	};
};
